import { API_BASE_URL } from "@/config";
import { validateToken } from '@/utils/auth';
import { useToast } from "@/components/Toast/useToast";
import Swal from 'sweetalert2';

export default {
  name: 'ReasonsForUsage',
  setup() {
    const { showSuccess, showError, showWarning } = useToast();
    
    return {
      showSuccess,
      showError,
      showWarning
    };
  },

  data: () => ({
    reasons: [] 
  }),

  computed: {
    workflow_id() {
      return this.$store.getters.get_workflow_id;
    }
  },

  methods: {
    // Fetch reasons from the backend
    get_reasons() {
      const TOKEN = localStorage.getItem('token');
      const GET_REASONS_API = `${API_BASE_URL}/verify_api/workflow_management_api/get_reasons_api/?workflow_id=${this.workflow_id}`;

      $.ajax({
        url: GET_REASONS_API,
        method: 'GET',
        contentType: 'application/json',
        headers: {
          Authorization: `Token ${TOKEN}`
        },
        beforeSend: () => {
          // Remove loading modal for better UX
        },
        success: (response) => {
          // Initialize reasons with server data
          this.reasons = response.map(reason => ({
            ...reason,
            is_selected: false // Initially, mark all reasons as not selected
          }));
          // Fetch and update selected reasons
          this.get_selected_reasons();
        },
        error: () => {
          this.showError('Failed to fetch reasons.');
        }
      });
    },

    // Fetch the selected reasons and update the is_selected value for reasons
    get_selected_reasons() {
      const TOKEN = localStorage.getItem('token');
      const GET_SELECTED_REASONS_API = `${API_BASE_URL}/verify_api/workflow_management_api/get_selected_reasons_api/?workflow_id=${this.workflow_id}`;

      $.ajax({
        url: GET_SELECTED_REASONS_API,
        method: 'GET',
        contentType: 'application/json',
        headers: {
          Authorization: `Token ${TOKEN}`
        },
        beforeSend: () => {
          // Remove loading modal for better UX
        },
        success: (response) => {
          // Update reasons with the selected reasons
          response.forEach(selectedReason => {
            const reason = this.reasons.find(r => r.id === selectedReason.reason_id);
            if (reason) {
              reason.is_selected = true; // Mark the reason as selected
            }
          });
        },
        error: () => {
          this.showError('Failed to fetch selected reasons.');
        }
      });
    },

    async toggle_selection(reason) {  
      const TOKEN = localStorage.getItem('token');
      const BASE_URL = API_BASE_URL;
      const ENDPOINTS = {
          SELECT_REASONS: `${BASE_URL}/verify_api/workflow_management_api/select_usage_reasons_api/${this.workflow_id}`,
          CHECK_STATUS: `${BASE_URL}/verify_api/workflow_management_api/get_workflow_status/${this.workflow_id}`,
          UPDATE_STATUS: `${BASE_URL}/verify_api/workflow_management_api/edit_workflow_api/`
      };
  
      const original_state = reason.is_selected;
      let showSuccessMessage = false; // Default to not showing success message
  
      try {
          // First check the workflow status
          const statusResponse = await $.ajax({
              url: ENDPOINTS.CHECK_STATUS,
              method: 'GET',
              headers: {
                  Authorization: `Token ${TOKEN}`
              }
          });
  
          // If workflow is approved, show confirmation dialog
          if (statusResponse.status === 'approved') {
              const result = await Swal.fire({
                title: 'Workflow Status Change',
                text: 'This workflow is currently approved. Making changes will set it back to incomplete status. Do you want to continue?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Yes, Continue',
                cancelButtonText: 'Cancel',
                reverseButtons: true
              });
  
              if (!result.isConfirmed) {
                  reason.is_selected = !original_state;
                  return;
              }
  
              showSuccessMessage = true; 

              await $.ajax({
                  url: ENDPOINTS.UPDATE_STATUS,
                  method: 'POST',
                  contentType: 'application/json',
                  data: JSON.stringify({
                      workflow_id: this.workflow_id,
                      status: 'incomplete'
                  }),
                  headers: {
                      Authorization: `Token ${TOKEN}`
                  }
              });
          }
  
          // Proceed with saving the reason selection
          const response = await $.ajax({
              url: ENDPOINTS.SELECT_REASONS,
              method: 'POST',
              contentType: 'application/json',
              headers: {
                  Authorization: `Token ${TOKEN}`
              },
              data: JSON.stringify({
                  reason_id: reason.id,
                  is_selected: reason.is_selected
              })
          });
  
          if (response.status === "success") {
              reason.is_selected = original_state;
              
              // Show success message only when confirming status change from approved
              if (showSuccessMessage) {
                  this.showSuccess('Selection updated successfully');
              }
          } else {
              this.showError('Failed to update selection.');
              // Revert selection on failure
              reason.is_selected = !original_state;
          }
  
      } catch (error) {
          let error_message = 'Failed to save selected reasons';
          if (error.responseJSON) {
              const ERROR_RESPONSE = error.responseJSON;
              if (ERROR_RESPONSE && ERROR_RESPONSE.error) {
                  error_message = ERROR_RESPONSE.error;
              }
          }
          
          this.showError(error_message);
  
          // Revert selection on error
          reason.is_selected = !original_state;
      }
  },

    // Check if at least one reason is selected
    has_selected_reason() {
      return this.reasons.some(reason => reason.is_selected);
    },

    // Navigate to the next page only if at least one reason is selected
    next() {
      if (!this.has_selected_reason()) {
        this.showWarning('Please select at least one reason before proceeding.');
        return;
      }
      this.$router.push({ name: 'workflow_router' });
    }
  },

  mounted() {
    if (!validateToken(this.$router)) {
      return;
  }
    this.get_reasons();
  }
};
